import AddCardIcon from '@mui/icons-material/AddCard';
import DownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Button,
  Menu,
  MenuItem,
} from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { getIconComponent } from './getIcon';
import i18n from 'FRONTEND/utils/i18n';
import { getPatientPath, PATIENT_PATH } from 'FRONTEND/utils/routes';

// MUI 4 theme does not provide shades of the primary color (only light, main and dark) so we need to build them ourselves until Mui 5 is the standard here.
const HIGHLIGHT_HOVER_BACKGROUND_COLOR = '#c6e0f5';
const HIGHLIGHT_BACKGROUND_COLOR = '#e8edf1';

const Actions = ({
  screenXS,
  isExpectingCreation,
  actions = [],
  transitions = [],
  isExpectingEdition,
  patientLogicalId,
  locale,
  buttonsDisabled,
  onControlledCancelClick,
  onActionClick,
  onTransitionClick,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const displayedTransition = useSelector((state) => state.consultationView.displayedTransition);

  const renderButtons = (buttons) => {
    if (!buttons?.length) {
      return null;
    }

    return (
      <div className='actionBar-buttonList' key='actions'>
        {buttons.map(({ label, disabled, href, form, toggle, target, draft, highlight, icon, isTransition, id, url, noExam }) => {
          // Cancel buttons when creating/updating patient during calendar appointment creation.
          const isCreatePatientCancelButton = (isExpectingCreation && href === PATIENT_PATH) || (isExpectingEdition && href === getPatientPath(patientLogicalId));
          const labelString = typeof label === 'string' ? label : label[locale];
          if ((isCreatePatientCancelButton)) {
            return (
              <Button
                key={typeof label === 'string' ? label : label.en}
                name='action'
                component='button'
                type='button'
                onClick={onControlledCancelClick}
                color={highlight ? 'primary' : 'inherit'}
                variant='contained'
                startIcon={getIconComponent(icon)}
              >
                {labelString}
              </Button>
            );
          }
          return (
            <Button
              key={typeof label === 'string' ? label : label.en}
              name='action'
              component='button'
              type='button'
              disabled={disabled || buttonsDisabled || id === displayedTransition}
              href={href}
              form={form}
              data-toggle={toggle}
              data-target={target}
              onClick={(event) => {
                handleMenuClose();
                if (isTransition) {
                  onTransitionClick(event, id, url, noExam);
                } else {
                  onActionClick(event);
                }
              }}
              data-pjax
              data-draft={draft}
              color={highlight ? 'primary' : 'inherit'}
              variant='contained'
              startIcon={getIconComponent(icon)}
            >
              {labelString}
            </Button>
          );
        })}
      </div>
    );
  };


  const renderActionMenuItems = (actions) => {
    if (!actions?.length) {
      return null;
    }

    const actionMenuItemSx = (highlight) => ({
      bgcolor: highlight ? HIGHLIGHT_BACKGROUND_COLOR : 'default',
      width: '100%',
      '&:hover': {
        bgcolor: highlight ? HIGHLIGHT_HOVER_BACKGROUND_COLOR : 'default',
      },
    });

    return actions.map(({ label, disabled, href, form, toggle, target, draft, highlight, icon }) => {
      // Cancel buttons when creating/updating patient during calendar appointment creation.
      return ((isExpectingCreation && href === PATIENT_PATH) || (isExpectingEdition && href === getPatientPath(patientLogicalId))) ? (
        <MenuItem
          key={typeof label === 'string' ? label : label.en}
          component='button'
          onClick={onControlledCancelClick}
          className='actionBar-button-dropDown'
          variant='contained'
          sx={actionMenuItemSx(highlight)}
        >
          {typeof label === 'string' ? label : label[locale]}
        </MenuItem>
      ) : (
        <MenuItem
          key={typeof label === 'string' ? label : label.en}
          name='action'
          component='button'
          disabled={disabled || buttonsDisabled}
          href={href}
          form={form}
          data-toggle={toggle}
          data-target={target}
          onClick={(event) => {
            handleMenuClose();
            onActionClick(event);
          }}
          data-pjax
          data-draft={draft}
          className='actionBar-button-dropDown'
          variant='contained'
          sx={actionMenuItemSx(highlight)}
        >
          {icon && (
            <ListItemIcon>
              {getIconComponent(icon)}
            </ListItemIcon>
          )}

          {typeof label === 'string' ? label : label[locale]}
        </MenuItem>
      );
    });
  };

  const handleDropDownClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };


  const renderTransitionMenuItems = (transitions) => {
    if (!transitions?.length) {
      return null;
    }

    return [
      ...transitions.map(({ id, label, url, noExam, icon }) => (
        <MenuItem
          key={'menuItem' + id}
          component='button'
          onClick={(event) => {
            handleMenuClose();
            onTransitionClick(event, id, url, noExam);
          }}
          disabled={id === displayedTransition}
          className='actionBar-button-dropDown'
          sx={(theme) => ({
            bgcolor: theme.palette.grey[50],
            width: '100%',
            '&:hover': {
              bgcolor: theme.palette.grey[200],
            },
          })}
        >
          {icon && (
            <ListItemIcon>
              {getIconComponent(icon)}
            </ListItemIcon>
          )}
          {label}
        </MenuItem>
      )),
    ];
  };

  const [buttons, actionsInMenu, transitionsInMenu] = getButtonItems(screenXS, transitions, actions);

  return (
    <>
      {renderButtons(buttons)}
      {(transitionsInMenu.length + actionsInMenu.length > 0) && (
        <div className='actionBar-buttonList-dropDown'>
          <Button
            className='actionbar-menuButton'
            onClick={handleDropDownClick}
            variant='contained'
            startIcon={<AddCardIcon />}
            endIcon={<DownIcon />}
          >
            {!screenXS && i18n._('Actions')}
          </Button>
          <Menu
            className='actionbar-menu'
            open={Boolean(menuAnchorEl)}
            anchorEl={menuAnchorEl}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            disableAutoFocusItem
          >
            {renderActionMenuItems(actionsInMenu)}
            {renderTransitionMenuItems(transitionsInMenu)}
          </Menu>
        </div>
      )}
    </>
  );
};

/**
 * determines which buttons/menu items to display
 * @param {d} screenXS
 * @param {*} transitions
 * @param {*} actions
 * @returns
 */
const getButtonItems = (screenXS, transitions, actions) => {
  let buttons;
  let actionsInMenu;
  let transitionsInMenu;
  if (screenXS) {
  // small screen, put everything in the menu
    buttons = [];
    actionsInMenu = actions;
    transitionsInMenu = transitions;
  } else {
    const actionsInButtons = actions.filter(action => action.icon !== 'mark_email_unread');
    actionsInMenu = actions.filter(action => action.icon === 'mark_email_unread');

    let transitionsInButtons;
    // 2 or less, always display as buttons
    if (transitions.length <= 2) {
      transitionsInButtons = transitions;
      transitionsInMenu = [];
    } else {
      // all transitions in menu
      transitionsInButtons = [];
      transitionsInMenu = transitions;
    }

    // design decision: transitions as buttons should always be blue
    transitionsInButtons = transitionsInButtons.map(transition => {
      transition.isTransition = true;
      transition.highlight = true;
      return transition;
    });

    buttons = [...actionsInButtons, ...transitionsInButtons];

    // never display menu with a single item
    if (actionsInMenu.length + transitionsInMenu.length === 1) {
      buttons = [...actionsInMenu, ...buttons, ...transitionsInMenu];
      actionsInMenu = [];
      transitionsInMenu = [];
    }
  }
  return [buttons, actionsInMenu, transitionsInMenu];
};

export default Actions;
